import { syncService } from '@/services/sync.service';
import { unifiedSyncService } from '@/services/sync/UnifiedSyncService';
import { syncLock } from '@/services/sync/syncLock';
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';
import { getValidUserId } from '@/utils/validation';
import {
  SyncEventEmitter,
  SyncEventCallback,
  setupNetworkListener,
  setupPageVisibilityListener,
  setupPeriodicSync
} from './sync/sync-events';

export interface AutoSyncConfig {
  enabled: boolean;
  minInterval: number; // Minimum time between auto-syncs (ms)
  maxDataAge: number; // Max age before forcing sync (ms)
  onlyOnWifi: boolean;
  backgroundSync: boolean;
}

class SmartAutoSyncService {
  private config: AutoSyncConfig = {
    enabled: true,
    minInterval: 5 * 60 * 1000, // 5 minutes
    maxDataAge: 60 * 60 * 1000, // 1 hour
    onlyOnWifi: false,
    backgroundSync: true
  };

  private lastAutoSync = 0;
  private emitter = new SyncEventEmitter();
  private periodicId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    setupNetworkListener(() => this.evaluateAutoSync('network-reconnect'));
    setupPageVisibilityListener(() => this.evaluateAutoSync('page-focus'));
    this.startPeriodicSync();
  }

  private startPeriodicSync() {
    if (this.periodicId) clearInterval(this.periodicId);
    this.periodicId = setupPeriodicSync(this.config.minInterval, () => {
      if (this.config.enabled && this.config.backgroundSync) {
        this.evaluateAutoSync('periodic');
      }
    });
  }

  async performSmartSync(entities: string[]): Promise<void> {
    await this.evaluateAutoSync('manual-smart-sync', entities);
  }

  async evaluateAutoSync(trigger: string, entityNames?: string[]): Promise<void> {
    if (!this.shouldPerformAutoSync()) {
      return;
    }

    // Lock globale: i trigger concorrenti (anche cross-service) si agganciano
    // al ciclo già in corso invece di drenare la coda in parallelo.
    await syncLock.runExclusive(async () => {
      try {
        this.emitter.emit(`auto-sync-started-${trigger}`);

        OfflineLogger.info('SmartAutoSyncService.evaluateAutoSync', `Starting auto-sync triggered by: ${trigger}`);

        const userId = getValidUserId();
        if (!userId) {
          OfflineLogger.warn('SmartAutoSyncService.evaluateAutoSync', 'No user ID found');
          return;
        }

        const entitiesNeedingSync = await this.getEntitiesNeedingSync(userId, entityNames);

        if (entitiesNeedingSync.length === 0) {
          OfflineLogger.info('SmartAutoSyncService.evaluateAutoSync', 'No entities need sync');
          return;
        }

        const result = await unifiedSyncService.performUnifiedSync(entitiesNeedingSync);

        this.lastAutoSync = Date.now();

        const successCount = result.success ? result.entitiesSynced.length : 0;
        const totalRecords = result.recordsProcessed;

        OfflineLogger.info(
          'SmartAutoSyncService.evaluateAutoSync',
          `Auto-sync completed: ${successCount} entities synced, ${totalRecords} records processed, success: ${result.success}`
        );

        this.emitter.emit(`auto-sync-completed-${trigger}`);
      } catch (error) {
        OfflineLogger.error('SmartAutoSyncService.evaluateAutoSync', error);
        this.emitter.emit(`auto-sync-error-${trigger}`);
      }
    });
  }

  private shouldPerformAutoSync(): boolean {
    if (!this.config.enabled) return false;
    if (!networkService.isOnline()) return false;
    if (syncLock.isSyncing) return false;

    const timeSinceLastSync = Date.now() - this.lastAutoSync;
    if (timeSinceLastSync < this.config.minInterval) return false;

    if (this.config.onlyOnWifi && this.isOnMobileData()) return false;

    return true;
  }

  private async getEntitiesNeedingSync(userId: string, entities?: string[]): Promise<string[]> {
    const entityList =
      entities && entities.length > 0
        ? entities
        : [
            'categories',
            'products',
            'shops',
            'shoppingLists',
            'shoppingItems',
            'recurringProducts',
            'purchaseHistory'
          ];
    const entitiesNeedingSync: string[] = [];

    for (const entity of entityList) {
      try {
        const pendingCount = await syncService.getEntityPendingCount(entity);
        if (pendingCount > 0) {
          entitiesNeedingSync.push(entity);
          continue;
        }

        const needsSync = await unifiedSyncService.isEntitySyncNeeded(entity, userId);
        if (needsSync) {
          entitiesNeedingSync.push(entity);
          continue;
        }

        const lastSync = localStorage.getItem(`lastSync_${entity}`);
        if (!lastSync) {
          entitiesNeedingSync.push(entity);
          continue;
        }

        const dataAge = Date.now() - new Date(lastSync).getTime();
        if (dataAge > this.config.maxDataAge) {
          entitiesNeedingSync.push(entity);
        }
      } catch (error) {
        OfflineLogger.error('SmartAutoSyncService.getEntitiesNeedingSync', `Error checking ${entity}: ${error}`);
        entitiesNeedingSync.push(entity);
      }
    }

    return entitiesNeedingSync;
  }

  private isOnMobileData(): boolean {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      return connection?.type === 'cellular';
    }
    return false;
  }

  updateConfig(newConfig: Partial<AutoSyncConfig>): void {
    this.config = { ...this.config, ...newConfig };
    localStorage.setItem('autoSyncConfig', JSON.stringify(this.config));
    this.startPeriodicSync();
    OfflineLogger.info('SmartAutoSyncService.updateConfig', `Config updated: ${JSON.stringify(newConfig)}`);
  }

  getConfig(): AutoSyncConfig {
    return { ...this.config };
  }

  forceSync(reason: string = 'manual', entities?: string[]): Promise<void> {
    this.lastAutoSync = 0;
    return this.evaluateAutoSync(reason, entities);
  }

  addListener(callback: SyncEventCallback): () => void {
    return this.emitter.addListener(callback);
  }

  init(): void {
    try {
      const savedConfig = localStorage.getItem('autoSyncConfig');
      if (savedConfig) {
        this.config = { ...this.config, ...JSON.parse(savedConfig) };
        this.startPeriodicSync();
      }
    } catch (error) {
      OfflineLogger.error('SmartAutoSyncService.init', error);
    }
  }

  destroy(): void {
    this.emitter.clear();
  }
}

export const smartAutoSyncService = new SmartAutoSyncService();
smartAutoSyncService.init();
